import { Component, type ErrorInfo, type ReactNode } from "react";

/** Send a browser-side crash to the server log (no personal data: where, message, first stack lines). */
export function reportClientError(where: string, err: unknown, extra?: string): void {
  try {
    const e = err instanceof Error ? err : new Error(String(err));
    void fetch("/api/client-error", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      keepalive: true,
      body: JSON.stringify({
        where, message: e.message.slice(0, 500), stack: `${e.stack ?? ""}\n${extra ?? ""}`.slice(0, 2000),
        page: window.location.hash.slice(0, 100), ua: navigator.userAgent.slice(0, 200),
      }),
    }).catch(() => undefined);
  } catch {
    /* never throw from the reporter */
  }
}

let installed = false;
/** Errors outside React rendering (event handlers, promises) are reported too. */
export function installGlobalErrorReporting(): void {
  if (installed) return;
  installed = true;
  window.addEventListener("error", (e) => reportClientError("window", e.error ?? e.message));
  window.addEventListener("unhandledrejection", (e) => reportClientError("promise", e.reason));
}

interface Props { name: string; children: ReactNode; resetKey?: unknown; compact?: boolean }
interface State { error: Error | null }

/** One part of the app failing shows a friendly message there instead of blanking the whole page. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError(this.props.name, error, info.componentStack ?? "");
  }

  componentDidUpdate(prev: Props): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className={this.props.compact ? "error-box compact" : "panel error-box"} role="alert">
        <strong>Something went wrong {this.props.compact ? "here" : `on this ${this.props.name}`}.</strong>{" "}
        <span className="muted small">The rest of the app still works. It's been reported. ({this.state.error.message.slice(0, 160)})</span>
        <div>
          <button onClick={() => this.setState({ error: null })}>Try again</button>{" "}
          <button onClick={() => window.location.reload()}>Reload page</button>
        </div>
      </div>
    );
  }
}
